import "./ShowAllSketches.scss";

export interface ShowAllSketchItem {
  readonly id: string;
  readonly name: string;
  readonly selected?: boolean;
  readonly preview?: {
    readonly url: string;
    readonly kind?: string;
  } | null;
}

export interface ShowAllSketchesConfig {
  readonly items?: readonly ShowAllSketchItem[];
  readonly onSelectSketch?: (item: ShowAllSketchItem) => void;
  readonly onRenameSketch?: (
    item: ShowAllSketchItem,
    nextName: string
  ) => Promise<string | null> | string | null;
}

export interface ShowAllSketchesAPI {
  readonly getElement: () => HTMLElement;
  readonly setSketches: (items: readonly ShowAllSketchItem[]) => void;
  readonly setVisible: (visible: boolean) => void;
  readonly destroy: () => void;
}

export const createShowAllSketches = (
  config: ShowAllSketchesConfig = {}
): ShowAllSketchesAPI => {
  const { onSelectSketch, onRenameSketch } = config;
  const container = document.createElement("div");
  container.className =
    "dedicated-placeholder dedicated-placeholder--show-all dedicated-placeholder--sketches";

  container.innerHTML = `
    <div class="dedicated-placeholder__header">
      <h2 class="dedicated-placeholder__title">Sketches</h2>
      <p class="dedicated-placeholder__subtitle">
        Browse quick sketches linked to this project.
      </p>
    </div>
    <div class="dedicated-placeholder__body">
      <div class="dedicated-placeholder__grid" data-role="grid"></div>
      <div class="dedicated-placeholder__empty" data-role="empty">
        <p>No sketches available yet. Capture ideas in Sketch mode to see them here.</p>
      </div>
    </div>
  `;

  const gridEl = container.querySelector(
    '[data-role="grid"]'
  ) as HTMLElement;
  const emptyEl = container.querySelector(
    '[data-role="empty"]'
  ) as HTMLElement;

  let currentItems: readonly ShowAllSketchItem[] = config.items ?? [];
  let lastSelectedId: string | null = null;

  const applyItemName = (itemId: string, nextName: string): void => {
    currentItems = currentItems.map((item) =>
      item.id === itemId ? { ...item, name: nextName } : item
    );
    renderItems();
  };

  const renderItems = () => {
    gridEl.innerHTML = "";
    const items = currentItems ?? [];
    if (!items.length) {
      gridEl.style.display = "none";
      emptyEl.style.display = "flex";
      return;
    }

    gridEl.style.display = "grid";
    emptyEl.style.display = "none";
    let selectedId: string | null = null;
    let selectedCard: HTMLElement | null = null;

    for (const item of items) {
      const card = document.createElement("div");
      card.className = "dedicated-card";
      if (item.selected) {
        selectedId = item.id;
        selectedCard = card;
        card.classList.add("dedicated-card--selected");
        card.setAttribute("aria-current", "true");
      }
      if (onSelectSketch) {
        card.classList.add("dedicated-card--interactive");
        card.setAttribute("role", "button");
        card.tabIndex = 0;
        card.addEventListener("click", () => {
          onSelectSketch(item);
        });
        card.addEventListener("keydown", (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelectSketch(item);
          }
        });
      }

      const preview = document.createElement("div");
      preview.className = "dedicated-card__preview dedicated-card__preview--sketch";
      if (item.preview?.url) {
        preview.classList.add("dedicated-card__preview--image");
        const img = document.createElement("img");
        img.src = item.preview.url;
        img.alt = `${item.name} preview`;
        img.loading = "lazy";
        preview.appendChild(img);
      }

      const name = document.createElement("span");
      name.className = "dedicated-card__name";
      name.textContent = item.name;

      const footer = document.createElement("div");
      footer.className = "dedicated-card__footer";

      const renameButton = document.createElement("button");
      renameButton.className = "dedicated-card__rename";
      renameButton.type = "button";
      renameButton.textContent = "Rename";
      renameButton.addEventListener("click", async (event) => {
        event.preventDefault();
        event.stopPropagation();
        const nextRaw = window.prompt("Rename sketch", item.name);
        if (nextRaw === null) return;
        const nextName = nextRaw.trim();
        if (!nextName || nextName === item.name) return;

        let resolvedName: string | null = nextName;
        if (onRenameSketch) {
          try {
            resolvedName = await onRenameSketch(item, nextName);
          } catch {
            resolvedName = null;
          }
        }
        if (!resolvedName) return;
        applyItemName(item.id, resolvedName);
      });

      card.appendChild(preview);
      footer.appendChild(name);
      footer.appendChild(renameButton);
      card.appendChild(footer);
      gridEl.appendChild(card);
    }

    if (selectedId && selectedId !== lastSelectedId && selectedCard) {
      selectedCard.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
    lastSelectedId = selectedId;
  };

  const setSketches = (items: readonly ShowAllSketchItem[]) => {
    currentItems = [...items];
    renderItems();
  };

  const setVisible = (visible: boolean) => {
    container.style.display = visible ? "flex" : "none";
  };

  const destroy = () => {
    container.remove();
  };

  renderItems();

  return {
    getElement: () => container,
    setSketches,
    setVisible,
    destroy,
  };
};
